/**
 * Project Root Management Service
 * 
 * Handles multiple project root directories with support for both
 * File System Access API (browser) and Tauri file selection (desktop).
 * Provides complete state isolation between projects.
 */

// Simple browser-compatible EventEmitter
class EventEmitter {
  private events: Map<string, Set<Function>> = new Map();

  on(event: string, listener: Function): void {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    this.events.get(event)!.add(listener);
  }

  off(event: string, listener: Function): void {
    this.events.get(event)?.delete(listener);
  }

  emit(event: string, ...args: any[]): void {
    this.events.get(event)?.forEach(listener => listener(...args));
  }
}

export interface ProjectRoot {
  id: string;
  name: string;
  path: string;
  type: 'filesystem' | 'virtual' | 'tauri';
  handle?: FileSystemDirectoryHandle;
  lastAccessed: Date;
  metadata: {
    fileCount: number;
    totalSize: number;
    created: Date;
    modified: Date;
    preferences?: Record<string, any>;
  };
}

export interface UserProjectPreferences {
  defaultProjectId?: string;
  recentProjects: string[];
  maxRecentProjects: number;
  projectSettings: Record<string, ProjectSettings>;
}

export interface ProjectSettings {
  theme?: 'light' | 'dark' | 'system';
  editorPreferences?: {
    fontSize: number;
    tabSize: number;
    wordWrap: boolean;
  };
  customAlias?: string;
}

export class ProjectRootManager extends EventEmitter {
  private static instance: ProjectRootManager;
  private projects: Map<string, ProjectRoot> = new Map();
  private currentProjectId: string | null = null;
  private preferences: UserProjectPreferences;
  private dbPrefix = 'phrasewright_project_';

  private constructor() {
    super();
    this.preferences = {
      recentProjects: [],
      maxRecentProjects: 10,
      projectSettings: {}
    };
    this.loadProjects();
  }

  public static getInstance(): ProjectRootManager {
    if (!ProjectRootManager.instance) {
      ProjectRootManager.instance = new ProjectRootManager();
    }
    return ProjectRootManager.instance;
  }

  /**
   * Load saved projects from IndexedDB
   */
  private async loadProjects(): Promise<void> {
    try {
      const db = await this.openMetaDB();
      const transaction = db.transaction(['projects', 'preferences'], 'readonly');
      
      // Load projects
      const projectStore = transaction.objectStore('projects');
      const projectsRequest = projectStore.getAll();
      
      // Load preferences
      const prefStore = transaction.objectStore('preferences');
      const prefRequest = prefStore.get('user');
      
      const [projects, prefs] = await Promise.all([
        new Promise<ProjectRoot[]>((resolve, reject) => {
          projectsRequest.onsuccess = () => resolve(projectsRequest.result);
          projectsRequest.onerror = () => reject(projectsRequest.error);
        }),
        new Promise<UserProjectPreferences | undefined>((resolve, reject) => {
          prefRequest.onsuccess = () => resolve(prefRequest.result);
          prefRequest.onerror = () => reject(prefRequest.error);
        })
      ]);

      // Restore projects
      for (const project of projects) {
        this.projects.set(project.id, {
          ...project,
          lastAccessed: new Date(project.lastAccessed)
        });
      }

      // Restore preferences
      if (prefs) {
        this.preferences = prefs;
      }

      // Set current project to last accessed or default
      if (this.preferences.defaultProjectId && this.projects.has(this.preferences.defaultProjectId)) {
        this.currentProjectId = this.preferences.defaultProjectId;
      } else if (this.preferences.recentProjects.length > 0) {
        const recentId = this.preferences.recentProjects[0];
        if (this.projects.has(recentId)) {
          this.currentProjectId = recentId;
        }
      }

      db.close();
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  }

  /**
   * Open meta database for project management
   */
  private async openMetaDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('phrasewright_meta', 1);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Projects store
        if (!db.objectStoreNames.contains('projects')) {
          const projectStore = db.createObjectStore('projects', { keyPath: 'id' });
          projectStore.createIndex('name', 'name', { unique: false });
          projectStore.createIndex('lastAccessed', 'lastAccessed', { unique: false });
        }
        
        // Preferences store
        if (!db.objectStoreNames.contains('preferences')) {
          db.createObjectStore('preferences', { keyPath: 'id' });
        }
      };
    });
  }

  /**
   * Add a new project root using File System Access API
   */
  public async addProjectFromFilesystem(): Promise<ProjectRoot | null> {
    try {
      // Check if File System Access API is available
      if (!('showDirectoryPicker' in window)) {
        throw new Error('File System Access API not supported');
      }

      // Request directory access
      const handle = await (window as any).showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'documents'
      });

      // Generate project ID from handle name
      const id = this.generateProjectId(handle.name);
      
      // Check if project already exists
      if (this.projects.has(id)) {
        const existing = this.projects.get(id)!;
        existing.lastAccessed = new Date();
        await this.saveProject(existing);
        return existing;
      }

      // Create new project
      const project: ProjectRoot = {
        id,
        name: handle.name,
        path: handle.name,
        type: 'filesystem',
        handle,
        lastAccessed: new Date(),
        metadata: {
          fileCount: 0,
          totalSize: 0,
          created: new Date(),
          modified: new Date()
        }
      };

      // Calculate metadata
      await this.updateProjectMetadata(project);

      // Save and add project
      this.projects.set(id, project);
      await this.saveProject(project);
      
      // Update recent projects
      this.addToRecentProjects(id);
      
      this.emit('project-added', project);
      return project;
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Failed to add project from filesystem:', error);
        this.emit('error', { type: 'add-project', error });
      }
      return null;
    }
  }

  /**
   * Add a virtual project (no filesystem access)
   */
  public async addVirtualProject(name: string): Promise<ProjectRoot> {
    const id = this.generateProjectId(name);
    
    const project: ProjectRoot = {
      id,
      name,
      path: `virtual://${name}`,
      type: 'virtual',
      lastAccessed: new Date(),
      metadata: {
        fileCount: 0,
        totalSize: 0,
        created: new Date(),
        modified: new Date()
      }
    };

    this.projects.set(id, project);
    await this.saveProject(project);
    this.addToRecentProjects(id);
    
    this.emit('project-added', project);
    return project;
  }

  /**
   * Switch to a different project
   */
  public async switchProject(projectId: string): Promise<boolean> {
    if (!this.projects.has(projectId)) {
      console.error(`Project ${projectId} not found`);
      return false;
    }

    const previousId = this.currentProjectId;
    const project = this.projects.get(projectId)!;

    // Update last accessed
    project.lastAccessed = new Date();
    await this.saveProject(project);

    // Switch current project
    this.currentProjectId = projectId;
    this.addToRecentProjects(projectId);

    // Emit event for UI updates
    this.emit('project-switched', {
      previous: previousId,
      current: projectId,
      project
    });

    // Clear any cached data from previous project
    if (previousId && previousId !== projectId) {
      await this.unloadProject(previousId);
    }

    return true;
  }

  /**
   * Get current project
   */
  public getCurrentProject(): ProjectRoot | null {
    if (!this.currentProjectId) return null;
    return this.projects.get(this.currentProjectId) || null;
  }

  /**
   * Get all projects
   */
  public getAllProjects(): ProjectRoot[] {
    return Array.from(this.projects.values());
  }

  /**
   * Get recent projects
   */
  public getRecentProjects(): ProjectRoot[] {
    return this.preferences.recentProjects
      .map(id => this.projects.get(id))
      .filter(Boolean) as ProjectRoot[];
  }

  /**
   * Remove a project
   */
  public async removeProject(projectId: string): Promise<boolean> {
    if (!this.projects.has(projectId)) {
      return false;
    }

    // Cannot remove current project
    if (this.currentProjectId === projectId) {
      console.error('Cannot remove current project');
      return false;
    }

    const project = this.projects.get(projectId)!;
    this.projects.delete(projectId);

    // Remove from recent projects
    this.preferences.recentProjects = this.preferences.recentProjects.filter(
      id => id !== projectId
    );

    // Delete from database
    await this.deleteProject(projectId);

    // Clean up project database
    await this.cleanupProjectDatabase(projectId);

    this.emit('project-removed', project);
    return true;
  }

  /**
   * Update project metadata
   */
  private async updateProjectMetadata(project: ProjectRoot): Promise<void> {
    if (project.type === 'virtual') {
      // Virtual projects get metadata from IndexedDB
      const db = await this.openProjectDB(project.id);
      // Count files in the database
      // This would be implemented based on your storage structure
      db.close();
      return;
    }

    if (project.type === 'filesystem' && project.handle) {
      try {
        let fileCount = 0;
        let totalSize = 0;

        // Recursively count files
        const countFiles = async (dirHandle: FileSystemDirectoryHandle) => {
          for await (const entry of (dirHandle as any).values()) {
            if (entry.kind === 'file') {
              fileCount++;
              const file = await entry.getFile();
              totalSize += file.size;
            } else if (entry.kind === 'directory') {
              await countFiles(entry);
            }
          }
        };

        await countFiles(project.handle);
        
        project.metadata.fileCount = fileCount;
        project.metadata.totalSize = totalSize;
        project.metadata.modified = new Date();
      } catch (error) {
        console.error('Failed to update project metadata:', error);
      }
    }
  }

  /**
   * Save project to database
   */
  private async saveProject(project: ProjectRoot): Promise<void> {
    const db = await this.openMetaDB();
    const transaction = db.transaction(['projects'], 'readwrite');
    const store = transaction.objectStore('projects');
    
    // Don't save the handle directly
    const projectData = {
      ...project,
      handle: undefined
    };
    
    await new Promise<void>((resolve, reject) => {
      const request = store.put(projectData);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    db.close();
    await this.savePreferences();
  }

  /**
   * Delete project from database
   */
  private async deleteProject(projectId: string): Promise<void> {
    const db = await this.openMetaDB();
    const transaction = db.transaction(['projects'], 'readwrite');
    const store = transaction.objectStore('projects');
    
    await new Promise<void>((resolve, reject) => {
      const request = store.delete(projectId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    db.close();
  }

  /**
   * Save user preferences
   */
  private async savePreferences(): Promise<void> {
    const db = await this.openMetaDB();
    const transaction = db.transaction(['preferences'], 'readwrite');
    const store = transaction.objectStore('preferences');
    
    await new Promise<void>((resolve, reject) => {
      const request = store.put({ ...this.preferences, id: 'user' });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    db.close();
  }

  /**
   * Add project to recent list
   */
  private addToRecentProjects(projectId: string): void {
    // Remove if already in list
    this.preferences.recentProjects = this.preferences.recentProjects.filter(
      id => id !== projectId
    );
    
    // Add to front
    this.preferences.recentProjects.unshift(projectId);
    
    // Trim to max
    if (this.preferences.recentProjects.length > this.preferences.maxRecentProjects) {
      this.preferences.recentProjects = this.preferences.recentProjects.slice(
        0,
        this.preferences.maxRecentProjects
      );
    }
  }

  /**
   * Generate unique project ID
   */
  private generateProjectId(name: string): string {
    const base = name.toLowerCase().replace(/[^a-z0-9]/g, '-');
    return `${base}-${Date.now()}`;
  }

  /**
   * Open project-specific database
   */
  private async openProjectDB(projectId: string): Promise<IDBDatabase> {
    const dbName = `${this.dbPrefix}${projectId}`;
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, 1);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        // Create project-specific stores
        if (!db.objectStoreNames.contains('files')) {
          db.createObjectStore('files', { keyPath: 'id' });
        }
      };
    });
  }

  /**
   * Unload project data to free memory
   */
  private async unloadProject(projectId: string): Promise<void> {
    // This would clear any cached data for the project
    // Implementation depends on your caching strategy
    this.emit('project-unloaded', projectId);
  }

  /**
   * Clean up project database
   */
  private async cleanupProjectDatabase(projectId: string): Promise<void> {
    const dbName = `${this.dbPrefix}${projectId}`;
    await new Promise<void>((resolve, reject) => {
      const request = indexedDB.deleteDatabase(dbName);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get project settings
   */
  public getProjectSettings(projectId: string): ProjectSettings {
    return this.preferences.projectSettings[projectId] || {};
  }

  /**
   * Update project settings
   */
  public async updateProjectSettings(
    projectId: string,
    settings: Partial<ProjectSettings>
  ): Promise<void> {
    this.preferences.projectSettings[projectId] = {
      ...this.preferences.projectSettings[projectId],
      ...settings
    };
    await this.savePreferences();
    this.emit('settings-updated', { projectId, settings });
  }

  /**
   * Request permission for a project's directory handle
   */
  public async requestPermission(projectId: string): Promise<boolean> {
    const project = this.projects.get(projectId);
    if (!project || project.type !== 'filesystem' || !project.handle) {
      return false;
    }

    try {
      const permission = await (project.handle as any).requestPermission({
        mode: 'readwrite'
      });
      return permission === 'granted';
    } catch (error) {
      console.error('Failed to request permission:', error);
      return false;
    }
  }

  /**
   * Check permission status for a project
   */
  public async checkPermission(projectId: string): Promise<'granted' | 'denied' | 'prompt' | null> {
    const project = this.projects.get(projectId);
    if (!project || project.type !== 'filesystem' || !project.handle) {
      return null;
    }

    try {
      return await (project.handle as any).queryPermission({ mode: 'readwrite' });
    } catch (error) {
      console.error('Failed to check permission:', error);
      return null;
    }
  }
}

// Export singleton instance getter
export const getProjectRootManager = () => ProjectRootManager.getInstance();